import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalLogisticsApi from "../services/portalLogisticsApi";
import "../styles/PortalBusiness.css";

function fmtMoney(v) {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function badgeClass(status) {
  if (!status) return "ucx-portal-badge";
  const s = String(status).toLowerCase();
  if (s === "approved" || s === "delivered") return "ucx-portal-badge ucx-portal-badge-success";
  if (s === "pending") return "ucx-portal-badge ucx-portal-badge-warn";
  if (s === "rejected" || s === "cancelled") return "ucx-portal-badge ucx-portal-badge-danger";
  return "ucx-portal-badge";
}

export default function PortalShipmentDetailPage() {
  const { id } = useParams();
  const shipmentId = Number(id);
  const navigate = useNavigate();

  const links = useMemo(
    () => [
      { label: "Dashboard", to: "/portal/dashboard", perm: "portal.dashboard.read" },
      { label: "POS Terminals", to: "/portal/pos/terminals", perm: "pos.terminal.read" },
      { label: "POS Inventory", to: "/portal/pos-inventory", perm: "pos.inventory.submit.read" },
      { label: "Accounting", to: "/portal/accounting", perm: "accounting.journals.read" },
      { label: "Purchasing", to: "/portal/purchasing", perm: "purchasing.po.read" },
      { label: "Shipments", to: "/portal/shipments", perm: "logistics.shipments.read" },
    ],
    []
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [shipment, setShipment] = useState(null);
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);

  const [note, setNote] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await portalLogisticsApi.getShipment(shipmentId);
      if (!res?.ok) throw new Error(res?.error || "load_failed");

      // support multiple response shapes
      const sh = res.shipment || res.row || res.data || res;
      const its = res.items || sh.items || [];
      const evs = res.events || sh.events || [];

      setShipment(sh);
      setItems(its);
      setEvents(evs);
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to load shipment");
    } finally {
      setLoading(false);
    }
  }

  async function request(action) {
    setLoading(true);
    setError("");
    try {
      const res = await portalLogisticsApi.requestAction(shipmentId, action, { notes: note });
      if (!res?.ok) throw new Error(res?.error || "request_failed");
      setNote("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to submit request");
    } finally {
      setLoading(false);
    }
  }

  async function addNote() {
    if (!note.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await portalLogisticsApi.addNote(shipmentId, { notes: note });
      if (!res?.ok) throw new Error(res?.error || "note_failed");
      setNote("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to add note");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const pendingStatus = shipment?.pending_status || shipment?.pendingStatus;
  const pendingAction = shipment?.pending_action || shipment?.pendingAction;

  const canRequest = !pendingStatus || String(pendingStatus).toLowerCase() !== "pending";

  const status = shipment?.status || "";
  const canShip = canRequest && (status === "draft" || status === "ready");
  const canArrive = canRequest && status === "in_transit";
  const canReceive = canRequest && (status === "in_transit" || status === "arrived");
  const canCancel = canRequest && status !== "delivered" && status !== "cancelled";

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav
          title="Shipment Detail"
          subtitle="Timeline, items, and action requests (admin approvals enforced)"
          links={links}
        />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">
                Shipment {shipment?.shipment_no || shipment?.shipmentNo || `#${shipmentId}`}
              </h1>
              <p className="ucx-portal-sub">
                Status: <span className={badgeClass(shipment?.status)}>{shipment?.status || "-"}</span>{" "}
                {pendingStatus ? (
                  <>
                    {" "}
                    | Pending:{" "}
                    <span className={badgeClass(pendingStatus)}>
                      {pendingStatus} {pendingAction ? `(${pendingAction})` : ""}
                    </span>
                  </>
                ) : null}
              </p>
            </div>
            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => navigate("/portal/shipments")}>
                Back
              </button>
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={load}>
                Reload
              </button>
            </div>
          </div>

          {error ? <div className="ucx-portal-error">{error}</div> : null}
          {loading ? <div className="ucx-muted">Loading…</div> : null}

          {pendingStatus && String(pendingStatus).toLowerCase() === "pending" ? (
            <div className="ucx-portal-warn danger" style={{ marginBottom: 12 }}>
              This shipment has a pending request awaiting admin approval. New action requests are disabled until it is reviewed.
            </div>
          ) : null}

          <div className="ucx-portal-grid-2">
            <div className="ucx-portal-card-large">
              <div className="ucx-portal-panel-title">Summary</div>
              <div className="ucx-portal-results">
                <div className="ucx-portal-result">
                  <div className="ucx-portal-result-name">Route</div>
                  <div className="ucx-muted">
                    Origin branch #{shipment?.origin_branch_id || shipment?.originBranchId} → Destination branch #
                    {shipment?.destination_branch_id || shipment?.destinationBranchId}
                  </div>
                </div>
                <div className="ucx-portal-result">
                  <div className="ucx-portal-result-name">Locations</div>
                  <div className="ucx-muted">
                    Origin loc #{shipment?.origin_location_id || shipment?.originLocationId}{" "}
                    {shipment?.transit_location_id || shipment?.transitLocationId
                      ? `→ Transit loc #${shipment?.transit_location_id || shipment?.transitLocationId} `
                      : ""}
                    → Dest loc #{shipment?.destination_location_id || shipment?.destinationLocationId}
                  </div>
                </div>
                <div className="ucx-portal-result">
                  <div className="ucx-portal-result-name">Carrier / Tracking</div>
                  <div className="ucx-muted">
                    {shipment?.carrier_name || shipment?.carrierName || "-"} | {shipment?.tracking_ref || shipment?.trackingRef || "-"}
                  </div>
                </div>
                <div className="ucx-portal-result">
                  <div className="ucx-portal-result-name">Created</div>
                  <div className="ucx-muted">{fmtDate(shipment?.created_at || shipment?.createdAt)}</div>
                </div>
              </div>

              <div className="ucx-portal-toolbar" style={{ marginTop: 12 }}>
                <div className="ucx-portal-toolbar-hint">Requests (admin must approve)</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => request("SHIP")} disabled={!canShip || loading}>
                    Request Ship
                  </button>
                  <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => request("ARRIVE")} disabled={!canArrive || loading}>
                    Request Arrived
                  </button>
                  <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => request("RECEIVE")} disabled={!canReceive || loading}>
                    Request Receive
                  </button>
                  <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => request("CANCEL")} disabled={!canCancel || loading}>
                    Request Cancel
                  </button>
                </div>
              </div>

              <div className="ucx-portal-field" style={{ marginTop: 10 }}>
                <label>Optional note (included with request / note event)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note…" />
              </div>

              <div className="ucx-portal-actions" style={{ marginTop: 10, justifyContent: "flex-start" }}>
                <button className="ucx-btn ucx-btn-primary" type="button" onClick={addNote} disabled={loading || !note.trim()}>
                  Add Note to Timeline
                </button>
              </div>
            </div>

            <div className="ucx-portal-card-large">
              <div className="ucx-portal-panel-title">Items</div>
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th style={{ textAlign: "right" }}>Quantity</th>
                      <th>Batch</th>
                      <th>Meta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(items || []).length ? (
                      (items || []).map((it, idx) => (
                        <tr key={idx}>
                          <td>{it.product_id || it.productId || "-"}</td>
                          <td style={{ textAlign: "right" }}>{fmtMoney(it.quantity)}</td>
                          <td>{it.batch_id || it.batchId || "-"}</td>
                          <td className="ucx-muted" style={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {it.meta ? JSON.stringify(it.meta) : "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="ucx-muted">
                          No items on this shipment.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
            <div className="ucx-portal-lineitems-head">
              <div className="ucx-portal-panel-title">Timeline</div>
              <div className="ucx-portal-toolbar-hint">Includes requests + approvals + operational events.</div>
            </div>

            <div className="ucx-portal-timeline">
              {(events || []).length ? (
                (events || []).map((ev, idx) => (
                  <div className="ucx-portal-timeline-item" key={idx}>
                    <div className="ucx-portal-timeline-dot" />
                    <div className="ucx-portal-timeline-body">
                      <div className="ucx-portal-timeline-top">
                        <div className="ucx-portal-timeline-type">{ev.event_type || ev.eventType || "-"}</div>
                        <div className="ucx-portal-timeline-date">{fmtDate(ev.created_at || ev.createdAt)}</div>
                      </div>
                      {ev.notes || ev.note ? <div className="ucx-portal-timeline-note">{ev.notes || ev.note}</div> : null}
                      {ev.location_text || ev.locationText ? (
                        <div className="ucx-muted" style={{ fontSize: 12 }}>
                          {ev.location_text || ev.locationText}
                        </div>
                      ) : null}
                      {ev.actor ? (
                        <div className="ucx-muted" style={{ fontSize: 12 }}>
                          Actor: {typeof ev.actor === "string" ? ev.actor : JSON.stringify(ev.actor)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className="ucx-muted">No timeline events yet.</div>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
